import SaveIcon from '@mui/icons-material/Save';
import {
    Box,
    Button,
    ButtonGroup,
    Chip,
    CircularProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import { Check, ChevronDown, ListChecks, RotateCcw, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    pendingEntryCount,
    usePendingChanges,
} from '~/hooks/save/usePendingChanges';
import { useSavePatientChanges } from '~/hooks/save/useSavePatientChanges';
import { otherEditorsPresenceState } from '~/state/atoms/presenceAtoms';
import {
    saveOfferState,
    saveRequestedState,
    type SaveOffer,
} from '~/state/atoms/saveAtoms';
import DiscardChangesDialog from './DiscardChangesDialog';
import PendingChangesPanel from './PendingChangesPanel';
import SaveConflictDialog from './SaveConflictDialog';

/**
 * Persistent save status in the app header: a split button plus a
 * separate concurrent-editing warning.
 *
 * The split button is visible for the whole connector-backed session
 * (hidden for local-file/legacy sessions, which keep the leave-time
 * confirmations only): a quiet outlined "All saved" while clean, the
 * export-action blue (contained primary) "Save N changes" when dirty —
 * the primary click IS "save all", the common case. The chevron segment
 * opens a menu with the secondary options: review what is to be saved,
 * and discard-and-reload from the database (confirmed — irreversible).
 *
 * When the advisory presence registry reports other editors on this
 * patient, a separate warning chip appears to the RIGHT of the button:
 * saving may hit a conflict. Presence stays advisory — the save
 * workflow's optimistic concurrency is the real protection.
 */
export default function SaveStatusIndicator() {
    const intl = useIntl();
    const { state, canSave } = usePendingChanges();
    const save = useSavePatientChanges();
    const [panelOpen, setPanelOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [confirmingDiscard, setConfirmingDiscard] = useState(false);
    const saveRequested = useRecoilValue(saveRequestedState);
    const [saveOffer, setSaveOffer] = useRecoilState(saveOfferState);
    const otherEditors = useRecoilValue(otherEditorsPresenceState);
    const handledSaveRequestRef = useRef(saveRequested);

    const pendingCount = pendingEntryCount(state);

    // Requested auto-saves (approval transitions — see saveAtoms). This
    // component stays mounted for the whole session, so it is the one
    // reliable consumer, and running the save through THIS instance means
    // failures and conflicts surface in the standard dialog below.
    //
    // A request landing while a save is in flight is DEFERRED, not
    // dropped: it stays unconsumed until the running save finishes (the
    // effect re-runs when `save.saving` drops) — an approval granted
    // mid-save must still reach the database. A request with nothing
    // pending (e.g. the transition netted back to baseline) or with
    // saving unavailable is consumed and skipped.
    useEffect(() => {
        if (saveRequested === handledSaveRequestRef.current) {
            return;
        }
        if (save.saving) {
            return;
        }
        handledSaveRequestRef.current = saveRequested;
        if (canSave && pendingCount > 0) {
            void save.saveAll();
        }
    }, [saveRequested, canSave, save, pendingCount]);

    // An offer left open when the session ends is declined, so whatever
    // awaits it does not wait forever.
    useEffect(
        () => () =>
            setSaveOffer((open) => {
                open?.resolve(false);
                return null;
            }),
        [setSaveOffer]
    );

    const answerOffer = (offer: SaveOffer, saved: boolean): void => {
        offer.resolve(saved);
        setSaveOffer((open) => (open === offer ? null : open));
    };

    // A fully successful save closes the review panel (its list is empty
    // now); a panel opened deliberately while clean stays open.
    const report = save.report;
    useEffect(() => {
        if (report?.allSaved) {
            setPanelOpen(false);
        }
    }, [report]);

    if (!canSave) {
        return null;
    }

    // Two states carry unsaved work with no pending entry to count:
    // out-of-scope changes (dose display settings, an imported dose) and
    // changes the classifier could not attribute. Counting entries alone would
    // render the checkmark and "All saved" over exactly the work the
    // leave/close guard is about to warn on — affirming that nothing is
    // outstanding is the hazard, so the clean state consults hasChanges too.
    const dirty = pendingCount > 0 || (state?.hasChanges ?? false);
    const variant = dirty ? 'contained' : 'outlined';

    // A save retrying after a network failure says so: "Saving…" spinning
    // through a Wi-Fi switch reads as a hang, and the user needs to know
    // the changes are still pending, not lost.
    const label = save.saving
        ? intl.formatMessage({
              id: save.reconnecting
                  ? 'saveChanges.reconnecting'
                  : 'saveChanges.saving',
          })
        : pendingCount > 0
          ? intl.formatMessage(
                { id: 'saveChanges.saveCount' },
                { count: pendingCount }
            )
          : dirty
            ? // Unsaved work with nothing countable to save (out-of-scope or
              // unattributed changes): say so rather than offering "Save 0".
              intl.formatMessage({ id: 'saveChanges.unsavedChanges' })
            : intl.formatMessage({ id: 'saveChanges.allSaved' });

    const startIcon = save.saving ? (
        <CircularProgress size={12} color="inherit" />
    ) : dirty ? (
        <SaveIcon sx={{ fontSize: 15 }} />
    ) : (
        <Check size={14} />
    );

    const closeMenu = (): void => setMenuAnchor(null);

    return (
        <Box display="flex" alignItems="center" gap={1}>
            <Tooltip
                // On the group, not the button: the button is disabled
                // while saving, and a disabled element receives no hover.
                // describeChild: the text describes the group; without it
                // the tooltip would replace the group's accessible name.
                describeChild
                title={
                    save.saving && save.reconnecting
                        ? intl.formatMessage({
                              id: 'saveChanges.reconnectingTooltip',
                          })
                        : ''
                }
            >
                <ButtonGroup
                    size="small"
                    variant={variant}
                    color={dirty ? 'primary' : 'inherit'}
                    disableElevation
                    sx={{
                        // Quiet in the clean state; the theme's inherit-outline
                        // is too loud next to the logo.
                        ...(variant === 'outlined' && {
                            '& .MuiButton-root': {
                                color: 'text.secondary',
                                borderColor: 'divider',
                            },
                            // The group collapses the first segment's right
                            // border (transparent; currentColor on hover),
                            // which cuts the theme's primary hover outline
                            // short under the chevron — restore the full
                            // highlight on hover.
                            '& .MuiButtonGroup-firstButton:hover': {
                                borderRightColor: 'primary.main',
                            },
                        }),
                    }}
                >
                    <Button
                        data-testid="save-status-indicator"
                        startIcon={startIcon}
                        disabled={save.saving}
                        onClick={() => {
                            // Only offer to save when there is something a save can
                            // carry. Out-of-scope and unattributed changes make the
                            // header report unsaved work with a zero-entry pending
                            // set, and running saveAll there is a dead end: it
                            // sends nothing and cannot clear the state it is
                            // offering to clear. Open the review panel instead, so
                            // the user sees what is actually outstanding.
                            if (pendingCount > 0) {
                                void save.saveAll();
                            } else {
                                setPanelOpen(true);
                            }
                        }}
                        sx={{ textTransform: 'none', fontSize: 12, py: 0.25 }}
                    >
                        {label}
                    </Button>
                    <Button
                        data-testid="save-status-details"
                        aria-label={intl.formatMessage({
                            id: 'saveChanges.moreSaveActions',
                        })}
                        aria-haspopup="menu"
                        disabled={save.saving}
                        onClick={(event) => setMenuAnchor(event.currentTarget)}
                        sx={{ px: 0.25, minWidth: 26 }}
                    >
                        <ChevronDown size={14} />
                    </Button>
                </ButtonGroup>
            </Tooltip>
            <Menu
                anchorEl={menuAnchor}
                open={menuAnchor !== null}
                onClose={closeMenu}
            >
                <MenuItem
                    data-testid="save-menu-review"
                    onClick={() => {
                        closeMenu();
                        setPanelOpen(true);
                    }}
                >
                    <ListItemIcon>
                        <ListChecks />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ fontSize: 13 }}
                        primary={intl.formatMessage({
                            id: 'saveChanges.reviewChanges',
                        })}
                    />
                </MenuItem>
                <MenuItem
                    data-testid="save-menu-reload"
                    onClick={() => {
                        closeMenu();
                        setConfirmingDiscard(true);
                    }}
                >
                    <ListItemIcon>
                        <RotateCcw />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ fontSize: 13 }}
                        primary={intl.formatMessage({
                            id: 'saveChanges.reloadFromDb',
                        })}
                    />
                </MenuItem>
            </Menu>
            {otherEditors.count > 0 && (
                <Tooltip
                    title={intl.formatMessage(
                        {
                            // The sharper warning when someone's work is
                            // actually unsaved over there.
                            id:
                                otherEditors.editing > 0
                                    ? 'saveChanges.otherEditorsUnsaved'
                                    : 'saveChanges.otherEditors',
                        },
                        {
                            count:
                                otherEditors.editing > 0
                                    ? otherEditors.editing
                                    : otherEditors.count,
                        }
                    )}
                    placement="bottom-start"
                >
                    <Chip
                        data-testid="editor-presence-indicator"
                        color="warning"
                        variant="outlined"
                        size="small"
                        icon={<Users size={13} />}
                        label={intl.formatMessage(
                            {
                                id:
                                    otherEditors.editing > 0
                                        ? 'saveChanges.editorEditingCount'
                                        : 'saveChanges.editorCount',
                            },
                            { count: otherEditors.count }
                        )}
                        sx={{ px: 0.5, fontSize: 11, height: 24 }}
                    />
                </Tooltip>
            )}
            <PendingChangesPanel
                open={panelOpen || saveOffer !== null}
                onClose={() =>
                    saveOffer
                        ? answerOffer(saveOffer, false)
                        : setPanelOpen(false)
                }
                saving={save.saving}
                report={save.report}
                otherEditorCount={otherEditors.count}
                title={saveOffer?.title}
                message={saveOffer?.message}
                saveLabel={saveOffer?.saveLabel}
                onSaveAll={() => {
                    const offer = saveOffer;
                    void save.saveAll().then((result) => {
                        if (offer) {
                            answerOffer(offer, result?.allSaved === true);
                        }
                    });
                }}
                onDiscardAndReload={
                    saveOffer
                        ? undefined
                        : () => {
                              // Same teardown as the conflict dialog's reload
                              // path: drop any stale report so no dialog
                              // reopens over the reloading patient.
                              save.clearConflicts();
                              setPanelOpen(false);
                              save.discardAndReload();
                          }
                }
            />
            <DiscardChangesDialog
                open={confirmingDiscard}
                onCancel={() => setConfirmingDiscard(false)}
                onConfirm={() => {
                    setConfirmingDiscard(false);
                    save.clearConflicts();
                    setPanelOpen(false);
                    save.discardAndReload();
                }}
            />
            <SaveConflictDialog
                conflicts={save.conflicts}
                saving={save.saving}
                onOverwrite={(ids) => void save.overwrite(ids)}
                onDiscardAndReload={() => {
                    save.clearConflicts();
                    setPanelOpen(false);
                    save.discardAndReload();
                }}
                onClose={save.clearConflicts}
            />
        </Box>
    );
}
